import { useState } from 'react';
import { IconCheck, IconX } from '@/components/Icons';
import { useReviewDecision } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { formatDate } from '@/lib/format';
import { platformOfKey, REVIEW_CHIP, REVIEW_HINTS, REVIEW_LABELS, reviewViewOf } from '@/lib/receipts';
import type { Receipt, ReviewStatus } from '@/lib/types';
import './review.css';

interface ReviewPanelProps {
  receipt: Receipt;
  canDecide: boolean;
  onChange: (receipt: Receipt) => void;
}

const DONE: Record<ReviewStatus, string> = {
  APPROVED: 'Venda aprovada sem checkout.',
  REJECTED: 'Venda reprovada. O membro foi avisado.',
  REFUNDED: 'Venda marcada como reembolsada. O membro foi avisado.',
  PENDING: 'A venda voltou para a análise.',
};

/**
 * A conferência no detalhe do comprovante de venda: a situação e o motivo para
 * todos; aprovar sem checkout, reprovar (com o motivo) e desfazer para o
 * administrador.
 */
export function ReviewPanel({ receipt, canDecide, onChange }: ReviewPanelProps) {
  const decide = useReviewDecision();
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const view = reviewViewOf(receipt);
  const platform = platformOfKey(receipt.orderKey);

  async function send(status: ReviewStatus, reason?: string) {
    try {
      const { receipt: updated } = await decide.mutateAsync({ id: receipt.id, status, note: reason });
      onChange(updated);
      toast.success(DONE[status]);
      setRejecting(false);
      setNote('');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <section className="review-panel" data-status={view === 'LINKED' || view === 'CONFIRMED' ? 'APPROVED' : view} aria-label="Conferência">
      <div className="review-panel-head">
        <span className="label">Conferência</span>
        <span className={REVIEW_CHIP[view]}>{REVIEW_LABELS[view]}{view === 'LINKED' && platform ? ` · ${platform}` : ''}</span>
        {view !== 'PENDING' ? (
          <span className="muted">
            {receipt.reviewedByName ? `por ${receipt.reviewedByName}` : 'pelo sistema'}
            {receipt.reviewedAt ? ` em ${formatDate(receipt.reviewedAt.slice(0, 10))}` : ''}
          </span>
        ) : null}
      </div>
      {receipt.reviewNote ? <p className="review-note" style={{ margin: 0 }}>{receipt.reviewNote}</p> : null}
      <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>{REVIEW_HINTS[view]}</p>

      {canDecide ? (
        rejecting ? (
          <div className="review-reject">
            <label className="label" htmlFor={`panel-reject-${receipt.id}`}>Por que esta venda foi reprovada? O membro vai ver.</label>
            <textarea
              id={`panel-reject-${receipt.id}`}
              className="inp"
              value={note}
              maxLength={500}
              autoFocus
              onChange={(event) => setNote(event.target.value)}
            />
            <div className="review-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setRejecting(false)} disabled={decide.isPending}>Cancelar</button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void send('REJECTED', note.trim())}
                disabled={decide.isPending || note.trim().length < 3}
              >
                <IconX /> Reprovar
              </button>
            </div>
          </div>
        ) : (
          <div className="review-actions" style={{ marginLeft: 0, alignItems: 'center' }}>
            {/* A partir de 27/10/2026 a venda anexada não conta sem compra no checkout. */}
            {view === 'PENDING' && receipt.approvableWithoutOrder ? (
              <button type="button" className="btn" onClick={() => void send('APPROVED')} disabled={decide.isPending}>
                <IconCheck /> Aprovar sem checkout
              </button>
            ) : null}
            {view !== 'REJECTED' && view !== 'REFUNDED' ? (
              <button type="button" className="btn" onClick={() => setRejecting(true)} disabled={decide.isPending}>
                <IconX /> Reprovar
              </button>
            ) : null}
            {view !== 'PENDING' ? (
              <button type="button" className="btn btn-ghost" onClick={() => void send('PENDING')} disabled={decide.isPending}>
                {view === 'LINKED' ? 'Desvincular' : 'Voltar para análise'}
              </button>
            ) : null}
          </div>
        )
      ) : null}
    </section>
  );
}
